/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Badge,
    Col,
    Row,
    Table,
} from "reactstrap";
import { Loading } from "../../../Helper/Loading";
import {
    GatewayDeviceStatus,
    GatewayMeasurement,
    useGatewayStatus,
} from "../../../../hooks/useGatewayStatus";

interface RootState {
    devices: {
        current?: {
            UID?: string;
            type?: string;
        };
    };
}

const panelCss = css`
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 0.25rem;
`;

function humanizeKey(key: string): string {
    return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatUptime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return "N/A";
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
}

function formatPrimitive(value: unknown): string {
    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return String(value);
    return String(value);
}

/**
 * Recursively renders an arbitrary JSON value as labelled rows.
 * Tolerates added/removed/nested fields so diagnostics payloads never break the page.
 */
function DataTree({ data }: { data: unknown }) {
    if (data === null || data === undefined) {
        return <span className="text-muted">—</span>;
    }

    if (typeof data !== "object") {
        return <span className="fw-semibold">{formatPrimitive(data)}</span>;
    }

    if (Array.isArray(data)) {
        if (data.length === 0) return <span className="text-muted">—</span>;
        return (
            <span className="fw-semibold">
                {data
                    .map((v) => (v !== null && typeof v === "object" ? JSON.stringify(v) : formatPrimitive(v)))
                    .join(", ")}
            </span>
        );
    }

    const entries = Object.entries(data as Record<string, unknown>);
    if (entries.length === 0) return <span className="text-muted">—</span>;

    return (
        <div>
            {entries.map(([key, value]) => {
                const isNested = value !== null && typeof value === "object" && !Array.isArray(value);

                if (isNested) {
                    return (
                        <div key={key} className="mb-2">
                            <div className="fw-semibold text-primary">{humanizeKey(key)}</div>
                            <div className="ps-3">
                                <DataTree data={value} />
                            </div>
                        </div>
                    );
                }

                return (
                    <div
                        key={key}
                        className="d-flex justify-content-between align-items-start gap-3 border-bottom py-1"
                    >
                        <span className="text-muted">{humanizeKey(key)}</span>
                        <DataTree data={value} />
                    </div>
                );
            })}
        </div>
    );
}

function StateBadge({ state }: { state: string }) {
    const normalized = (state || "").toLowerCase();
    if (normalized === "online") return <Badge color="success">Online</Badge>;
    if (normalized === "offline") return <Badge color="danger">Offline</Badge>;
    return <Badge color="secondary">{state || "Unknown"}</Badge>;
}

function PingableBadge({ pingable, online }: { pingable?: boolean | null; online?: boolean }) {
    // An online device is by definition reachable, even if the gateway did not
    // include an explicit pingable flag for it.
    if (online || pingable === true) return <Badge color="success">Reachable</Badge>;
    if (pingable === false) return <Badge color="danger">No ping</Badge>;
    return <Badge color="secondary">Unknown</Badge>;
}

const diagCardCss = css`
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    padding: 0.75rem 1rem;
    height: 100%;
`;

const diagLabelCss = css`
    font-size: 0.72rem;
    font-weight: 600;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

function progressColor(pct: number): string {
    if (pct < 60) return "#28a745";
    if (pct < 80) return "#fd7e14";
    return "#dc3545";
}

function toNum(v: unknown): number | undefined {
    return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

interface Usage {
    total?: number;
    used?: number;
    free?: number;
    percent?: number;
}

/**
 * Fill in any missing usage figure from the others so a percentage can still be
 * shown when only a subset of {total, used, free, percent} is reported.
 */
function resolveUsage(u: Usage): Usage {
    let { total, used, free, percent } = u;
    if (total === undefined && used !== undefined && free !== undefined) total = used + free;
    if (used === undefined && total !== undefined && free !== undefined) used = total - free;
    if (percent === undefined && used !== undefined && total !== undefined && total > 0) percent = (used / total) * 100;
    if (used === undefined && percent !== undefined && total !== undefined) used = (percent / 100) * total;
    if (free === undefined && total !== undefined && used !== undefined) free = total - used;
    return { total, used, free, percent };
}

function ResourceBar({ label, percent, detail }: { label: string; percent?: number; detail?: string }) {
    const hasPct = percent !== undefined;
    const clamped = hasPct ? Math.min(Math.max(percent as number, 0), 100) : 0;
    const color = hasPct ? progressColor(percent as number) : "#dee2e6";
    return (
        <div css={diagCardCss}>
            <div className="d-flex justify-content-between align-items-baseline mb-1">
                <span css={diagLabelCss}>{label}</span>
                {hasPct ? (
                    <span css={css`font-size:0.85rem;font-weight:700;color:${color};`}>
                        {(percent as number).toFixed(1)}%
                    </span>
                ) : (
                    <span css={css`font-size:0.8rem;color:#adb5bd;`}>N/A</span>
                )}
            </div>
            <div css={css`height:8px;background:#e9ecef;border-radius:999px;overflow:hidden;`}>
                <div css={css`height:100%;width:${clamped}%;background:${color};border-radius:999px;transition:width 0.4s ease;`} />
            </div>
            {detail && <div css={css`font-size:0.68rem;color:#9ca3af;margin-top:4px;`}>{detail}</div>}
        </div>
    );
}

const DIAG_KNOWN_KEYS = new Set(["cpu", "memory", "disk", "runtime", "clock", "ts"]);

/**
 * Renders gateway diagnostics as compact resource cards (CPU / Memory / Disk)
 * plus a runtime/clock strip. Unknown keys fall back to the generic tree so the
 * view never breaks when fields are added or removed.
 */
function DiagnosticsView({ data }: { data: unknown }) {
    if (data === null || typeof data !== "object" || Array.isArray(data)) {
        return <div css={panelCss}><DataTree data={data} /></div>;
    }

    const d = data as Record<string, unknown>;
    const asObj = (v: unknown): Record<string, unknown> | undefined => {
        if (v !== null && typeof v === "object" && !Array.isArray(v)) {
            return v as Record<string, unknown>;
        }
        return undefined;
    };

    const cpu = asObj(d.cpu);
    const memory = asObj(d.memory);
    const disk = asObj(d.disk);
    const runtime = asObj(d.runtime);
    const clock = asObj(d.clock);

    const cpuUsage = cpu ? resolveUsage({ percent: toNum(cpu.percent), used: toNum(cpu.used), total: toNum(cpu.total) }) : undefined;
    const memUsage = memory
        ? resolveUsage({ total: toNum(memory.total_mb), used: toNum(memory.used_mb), free: toNum(memory.free_mb), percent: toNum(memory.percent) })
        : undefined;
    const diskUsage = disk
        ? resolveUsage({ total: toNum(disk.total_gb), used: toNum(disk.used_gb), free: toNum(disk.free_gb), percent: toNum(disk.percent) })
        : undefined;

    const gb = (mb?: number) => (mb !== undefined ? (mb / 1024).toFixed(1) : "?");
    const memDetail = memUsage && (memUsage.used !== undefined || memUsage.total !== undefined)
        ? `${gb(memUsage.used)} GB used of ${gb(memUsage.total)} GB`
        : undefined;

    const diskParts: string[] = [];
    if (diskUsage) {
        if (diskUsage.used !== undefined || diskUsage.total !== undefined) {
            diskParts.push(`${diskUsage.used?.toFixed(1) ?? "?"} GB used of ${diskUsage.total?.toFixed(1) ?? "?"} GB`);
        }
        if (diskUsage.free !== undefined) diskParts.push(`${diskUsage.free.toFixed(1)} GB free`);
    }
    const diskDetail = diskParts.length ? diskParts.join(" \u00b7 ") : undefined;

    const goroutines = runtime ? toNum(runtime.goroutines) : undefined;
    const version = runtime && typeof runtime.version === "string" ? runtime.version : undefined;
    const skewMs = clock ? toNum(clock.skew_ms) : undefined;
    const ntpSynced = clock && "ntp_synced" in clock ? Boolean(clock.ntp_synced) : undefined;
    const hasStrip = goroutines !== undefined || version !== undefined || skewMs !== undefined || ntpSynced !== undefined;

    const otherEntries = Object.entries(d).filter(([k]) => !DIAG_KNOWN_KEYS.has(k));
    const hasResourceCards = Boolean(cpu || memory || disk);

    return (
        <div>
            {hasResourceCards && (
                <Row className="g-2 mb-2">
                    {cpu && (
                        <Col md={4}>
                            <ResourceBar label="CPU" percent={cpuUsage?.percent} />
                        </Col>
                    )}
                    {memory && (
                        <Col md={4}>
                            <ResourceBar label="Memory" percent={memUsage?.percent} detail={memDetail} />
                        </Col>
                    )}
                    {disk && (
                        <Col md={4}>
                            <ResourceBar label="Disk" percent={diskUsage?.percent} detail={diskDetail} />
                        </Col>
                    )}
                </Row>
            )}

            {hasStrip && (
                <div css={css`
                    background:#fff;border:1px solid #dee2e6;border-radius:0.375rem;
                    padding:0.6rem 1rem;margin-bottom:0.5rem;
                    display:flex;flex-wrap:wrap;gap:1.25rem;align-items:center;
                `}>
                    {ntpSynced !== undefined && (
                        <div css={css`display:flex;flex-direction:column;align-items:center;`}>
                            <Badge color={ntpSynced ? "success" : "warning"}>
                                {ntpSynced ? "NTP synced" : "NTP not synced"}
                            </Badge>
                            {skewMs !== undefined && (
                                <span css={css`font-size:0.63rem;color:#9ca3af;margin-top:2px;`}>skew {skewMs} ms</span>
                            )}
                        </div>
                    )}
                    {goroutines !== undefined && (
                        <div css={css`display:flex;flex-direction:column;align-items:center;`}>
                            <span css={css`font-size:0.9rem;font-weight:600;`}>{goroutines}</span>
                            <span css={css`font-size:0.63rem;color:#9ca3af;`}>goroutines</span>
                        </div>
                    )}
                    {version !== undefined && (
                        <div css={css`display:flex;flex-direction:column;`}>
                            <span css={css`font-size:0.8rem;font-weight:600;font-family:monospace;`} title={version}>
                                {version.slice(0, 12)}
                            </span>
                            <span css={css`font-size:0.63rem;color:#9ca3af;`}>version</span>
                        </div>
                    )}
                </div>
            )}

            {otherEntries.length > 0 && (
                <div css={panelCss}>
                    <DataTree data={Object.fromEntries(otherEntries)} />
                </div>
            )}
        </div>
    );
}

export function GatewayStatusTab() {
    const device = useSelector((state: RootState) => state.devices.current);
    const clientId = device?.UID;

    const { data, isLoading, isError, dataUpdatedAt } = useGatewayStatus(clientId);

    const statusEntries = useMemo<[string, GatewayDeviceStatus][]>(() => {
        if (!data?.status) return [];
        return Object.entries(data.status).sort(([, a], [, b]) => {
            const rank = (s: GatewayDeviceStatus) => (s.state?.toLowerCase() === "online" ? 1 : 0);
            return rank(a) - rank(b); // offline first
        });
    }, [data?.status]);

    const measurements = useMemo<GatewayMeasurement[]>(() => data?.measurements ?? [], [data?.measurements]);

    // Accumulate the latest value per register id. New batches merge in and only
    // trigger a re-render when a value actually changes, so the table updates in
    // place (no flicker / disappear-reappear) as different registers report in.
    const [measurementsById, setMeasurementsById] = useState<Record<number, GatewayMeasurement>>({});

    useEffect(() => {
        if (measurements.length === 0) return;

        setMeasurementsById((prev) => {
            let changed = false;
            const next = { ...prev };
            measurements.forEach((m) => {
                const existing = next[m.id];
                if (!existing || existing.value !== m.value || existing.created !== m.created) {
                    next[m.id] = m;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [measurements]);

    const measurementRows = useMemo(
        () => Object.values(measurementsById).sort((a, b) => a.id - b.id),
        [measurementsById],
    );

    if (!device || device.type !== "ripening_room_gateway") {
        return <Alert color="warning">This device is not a gateway.</Alert>;
    }

    if (isLoading) {
        return <Loading />;
    }

    if (isError) {
        return (
            <Alert color="danger">
                Failed to load gateway status. Please try again.
            </Alert>
        );
    }

    const heartbeat = data?.heartbeat ?? null;
    const diagnostics = data?.diagnostics ?? null;
    const viaRipewise = data?.source === "ripewise";
    const onlineCount = statusEntries.filter(([, s]) => s.state?.toLowerCase() === "online").length;
    const hasAnyData = Boolean(heartbeat || diagnostics || statusEntries.length > 0 || measurementRows.length > 0);

    return (
        <div className="pt-3">
            {!hasAnyData && (
                <Alert color="info">
                    No gateway data received yet. Data appears here as soon as the gateway
                    publishes its status, diagnostics, heartbeat or measurements.
                </Alert>
            )}

            {/* Device status */}
            <section className="mb-4">
                <h3>Device Status</h3>
                {statusEntries.length === 0 ? (
                    <div css={panelCss} className="text-muted text-center">
                        No device status reported yet.
                    </div>
                ) : (
                    <Table hover responsive className="align-middle">
                        <thead>
                            <tr>
                                <th>{viaRipewise ? "Room" : "IP Address"}</th>
                                <th>State</th>
                                <th>Reachable</th>
                                <th>Last OK</th>
                                <th>Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {statusEntries.map(([key, info]) => (
                                <tr key={key}>
                                    <td className="font-monospace">{key}</td>
                                    <td><StateBadge state={info.state} /></td>
                                    <td>
                                        <PingableBadge
                                            pingable={info.pingable}
                                            online={info.state?.toLowerCase() === "online"}
                                        />
                                    </td>
                                    <td className="text-muted small">
                                        {info.last_ok ? new Date(info.last_ok).toLocaleString() : "—"}
                                    </td>
                                    <td className="text-danger small">
                                        {info.error ? (
                                            <span css={css`word-break: break-word;`}>{info.error}</span>
                                        ) : (
                                            "—"
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </section>

            {/* Diagnostics */}
            <section className="mb-4">
                <h3>Diagnostics</h3>
                <div css={panelCss} className="mb-2">
                    <Row>
                        <Col md={3}>
                            <strong>Uptime:</strong>{" "}
                            {typeof heartbeat?.uptime_s === "number" ? formatUptime(heartbeat.uptime_s) : "N/A"}
                        </Col>
                        <Col md={4}>
                            <strong>Last heartbeat:</strong>{" "}
                            {heartbeat?.ts ? new Date(heartbeat.ts).toLocaleString() : "N/A"}
                        </Col>
                        <Col md={5}>
                            <strong>Configured devices:</strong>{" "}
                            {statusEntries.length > 0 ? (
                                <>
                                    {onlineCount}/{statusEntries.length} online
                                </>
                            ) : (
                                "N/A"
                            )}
                        </Col>
                    </Row>
                </div>
                {diagnostics ? (
                    <DiagnosticsView data={diagnostics} />
                ) : (
                    <div css={panelCss} className="text-muted text-center">
                        No diagnostics reported yet.
                    </div>
                )}
            </section>

            {/* Measurements */}
            <section className="mb-4">
                <h3>Measurements</h3>
                {measurementRows.length === 0 ? (
                    <div css={panelCss} className="text-muted text-center">
                        No measurements reported yet.
                    </div>
                ) : (
                    <div css={panelCss}>
                        <Table size="sm" responsive hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Register ID</th>
                                    <th>Value</th>
                                    <th>Last update</th>
                                </tr>
                            </thead>
                            <tbody>
                                {measurementRows.map((m) => (
                                    <tr key={m.id}>
                                        <td className="font-monospace">{m.id}</td>
                                        <td className="fw-semibold">{m.value}</td>
                                        <td className="text-muted small">
                                            {m.created ? new Date(m.created).toLocaleString() : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </section>

            {hasAnyData && (
                <Row className="mt-2">
                    <Col className="d-flex justify-content-end align-items-center gap-2">
                        <Badge color="success">Live</Badge>
                        {viaRipewise && <small className="text-muted">Status via RipeWise</small>}
                        {dataUpdatedAt > 0 && (
                            <small className="text-muted">
                                Updated: {new Date(dataUpdatedAt).toLocaleTimeString()}
                            </small>
                        )}
                    </Col>
                </Row>
            )}
        </div>
    );
}

export default GatewayStatusTab;
